import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMaximize2, FiTrash2, FiX } from "react-icons/fi";

function formatFileSizeKb(sizeBytes) {
  return `${Math.round((sizeBytes || 0) / 1024)} KB`;
}

/**
 * Selected / uploaded file card: image preview (top) + file row (bottom), Attached Documents style.
 */
export default function ImageAnalysisFileList({
  selectedImageFile,
  uploadedImageFileId,
  uploadedImageFileName,
  previewUrl,
  onRemove,
  disabled = false,
}) {
  const { t } = useTranslation("common");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const fileDisplay = useMemo(() => {
    if (selectedImageFile) {
      return {
        name: selectedImageFile.name,
        subtitle: formatFileSizeKb(selectedImageFile.size),
      };
    }
    if (uploadedImageFileId) {
      return {
        name: uploadedImageFileName || t("imageAnalysis.uploaded"),
        subtitle: t("imageAnalysis.uploaded"),
      };
    }
    return null;
  }, [selectedImageFile, uploadedImageFileId, uploadedImageFileName, t]);

  useEffect(() => {
    setPreviewError(false);
    setLightboxOpen(false);
  }, [previewUrl]);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxOpen, closeLightbox]);

  if (!fileDisplay) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
        {t("imageAnalysis.noFileSelected")}
      </p>
    );
  }

  const showPreview = Boolean(previewUrl && !previewError);
  const imageAlt = fileDisplay.name || t("imageAnalysis.uploadPreview");

  return (
    <>
      <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
        {showPreview && (
          <div className="relative border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="absolute top-2 right-2 z-10 text-[11px] text-gray-100 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/55 hover:bg-black/70 backdrop-blur-sm"
              title={t("imageAnalysis.viewFullSize")}
            >
              <FiMaximize2 className="w-3.5 h-3.5 shrink-0" />
              <span>{t("imageAnalysis.viewFullSize")}</span>
            </button>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="block w-full p-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]"
            >
              <img
                src={previewUrl}
                alt={imageAlt}
                className="w-full max-h-[200px] object-contain"
                onError={() => setPreviewError(true)}
              />
            </button>
          </div>
        )}

        <div className="flex items-start justify-between gap-2 px-2 py-1.5">
          <div className="min-w-0">
            <div className="text-xs text-gray-700 dark:text-gray-200 truncate">
              {fileDisplay.name}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-300 truncate">
              {fileDisplay.subtitle}
            </div>
          </div>
          <button
            type="button"
            onClick={onRemove}
            title={t("deleteFile")}
            className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={disabled}
          >
            <FiTrash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {lightboxOpen && showPreview && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={imageAlt}
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label={t("close")}
          >
            <FiX className="w-6 h-6" />
          </button>
          <img
            src={previewUrl}
            alt={imageAlt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
